import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Sparkles, Images, LayoutGrid, Users, ShoppingBag, MessageSquare, ShieldCheck, Brain, Settings as SettingsIcon, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, mediaUrl } from "@/lib/api";

const PUBLIC = ["/login", "/signup", "/invite", "/forgot-password", "/reset-password", "/demo", "/", "/solutions", "/journal", "/art"];

const RailBtn = ({ to, active, icon: Icon, label, testid, badge }) => (
  <Link to={to} data-testid={testid}
    className={`relative flex w-full flex-col items-center gap-1 rounded-xl px-1 py-2 transition-colors duration-200 ${
      active ? "bg-[var(--brand)] text-white" : "text-[var(--ink-soft)] hover:bg-[var(--surface-2)]"}`}>
    <span className="relative">
      <Icon className="h-5 w-5" strokeWidth={active ? 2 : 1.6} />
      {badge && <span data-testid={`${testid}-badge`} className="absolute -right-1.5 -top-1 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white" />}
    </span>
    <span className="text-[10px] font-semibold leading-none">{label}</span>
  </Link>
);

// Vertical nav rail fixed to the right on tablet/desktop (lg+) for all authed pages.
export const StudioRail = () => {
  const { pathname } = useLocation();
  const { status, user, company, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const isSuper = user?.is_superadmin;

  useEffect(() => {
    if (status !== "authed") return;
    let live = true;
    const poll = async () => {
      try {
        const { data } = await api.get(isSuper ? "/admin/unread-total" : "/messages/unread-count");
        if (live) setUnread(data.unread || 0);
      } catch (e) { /* ignore */ }
    };
    poll();
    const t = setInterval(poll, 10000);
    return () => { live = false; clearInterval(t); };
  }, [status, isSuper, pathname]);

  const onPublic = PUBLIC.some((r) => (r === "/" ? pathname === "/" : pathname.startsWith(r)));
  if (status !== "authed" || !user || onPublic) return null;

  const isEditor = ["owner", "manager"].includes(user.role);
  const isRep = user.role === "sales_rep";
  const logo = company?.logo_url ? mediaUrl(company.logo_url) : "/customize-logo.jpg";

  const items = [
    { to: "/studio", icon: Sparkles, label: "Studio", testid: "rail-studio" },
    { to: "/history", icon: Images, label: "History", testid: "rail-history" },
  ];
  if (!isRep) {
    if (isEditor) items.push({ to: "/library", icon: LayoutGrid, label: "Library", testid: "rail-library" });
    if (isEditor) items.push({ to: "/team", icon: Users, label: "Team", testid: "rail-team" });
    items.push({ to: "/store", icon: ShoppingBag, label: "Store", testid: "rail-store" });
    if (isSuper) {
      items.push({ to: "/brain", icon: Brain, label: "Brain", testid: "rail-brain" });
      items.push({ to: "/console", icon: ShieldCheck, label: "Admin", testid: "rail-console", badge: unread > 0 });
    } else {
      items.push({ to: "/messages", icon: MessageSquare, label: "Inbox", testid: "rail-messages", badge: unread > 0 });
    }
  }

  return (
    <aside data-testid="studio-rail"
      className="hidden lg:flex lg:fixed lg:right-0 lg:top-0 lg:bottom-0 lg:z-40 lg:w-[84px] lg:flex-col lg:items-center lg:border-l lg:border-[var(--line)] lg:bg-white lg:py-4"
      style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 28px)" }}>
      <Link to="/studio" data-testid="rail-brand" title="Customize Studio"
        className="mb-3 flex h-12 w-12 items-center justify-center overflow-hidden rounded-xl border border-[var(--line)]">
        <img src={logo} alt="Customize Studio" className="h-full w-full object-contain p-1" />
      </Link>
      <nav className="thin-scroll flex w-full flex-1 flex-col items-center gap-1 overflow-y-auto px-2">
        {items.map((it) => (
          <RailBtn key={it.to} {...it} active={pathname === it.to} />
        ))}
      </nav>
      <div className="mt-2 flex w-full flex-col items-center gap-1 border-t border-[var(--line)] px-2 pt-3">
        {user.role === "owner" && (
          <RailBtn to="/settings" icon={SettingsIcon} label="Settings" active={pathname === "/settings"} testid="rail-settings" />
        )}
        <button data-testid="rail-logout" onClick={() => { logout(); navigate("/"); }}
          className="flex w-full flex-col items-center gap-1 rounded-xl px-1 py-2 text-[var(--ink-soft)] transition-colors duration-200 hover:bg-[var(--surface-2)]">
          <LogOut className="h-5 w-5" strokeWidth={1.6} />
          <span className="text-[10px] font-semibold leading-none">Logout</span>
        </button>
      </div>
    </aside>
  );
};
